import React from "react";
import { useTranslation } from "react-i18next";

import { UseCases } from "@planzone/core";
import {
  useAuthentication,
  useEditAllocation,
  useEditTask,
} from "@planzone/view-models";
import { allocation, task as taskMock } from "@planzone/dto";
import { useModal } from "@root/client/modal";
import { TaskCompletionStatus } from "@common/taskCompletionStatus";
import { ScrollingText } from "@common/scrollingText";
import { RecordList } from "../recordList";
import { Props } from "./types";
import styles from "./styles.scss";
import { isLastIncompleteAllocation } from "@pages/personalSpacePage/timeSheet/root/project/utils";

function Task({
  project,
  task,
  resource,
  depth = 1,
  areDetailsDisplayed,
  isReloading,
  showCompletionColumn,
  timesheetDeadline,
  resourceRemoved,
}: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { editTask } = useEditTask({ ...taskMock, ...task });
  const { account } = useAuthentication();
  const { editAllocation } = useEditAllocation({ ...allocation, uuid: "" });
  const isDone = task.percentComplete === 100;
  const userAllocation = task?.allocationData?.find(
    (alloc) => alloc.resourceUuid === resource.uuid,
  );

  return (
    <div className={styles.list}>
      <div className={styles.main}>
        <div
          className={styles.header}
          style={{ paddingLeft: `${depth === 0 ? 4 : (depth - 1) * 3 + 4}rem` }}
        >
          <div
            style={{ cursor: resourceRemoved ? "default" : "pointer" }}
            onClick={
              resourceRemoved
                ? undefined
                : (e) => {
                    e.stopPropagation();

                    userAllocation
                      ? !isDone &&
                        (task.allocationData.length === 1 ||
                          isLastIncompleteAllocation(
                            task.allocationData,
                            userAllocation,
                          ))
                        ? pushView({
                            key: UseCases.markTaskAsCompleted,
                            data: { task: { ...taskMock, ...task }, project },
                          })
                        : editAllocation({
                            allocation: {
                              ...allocation,
                              ...userAllocation,
                              resource,
                            },
                            nextAllocation: {
                              ...allocation,
                              ...userAllocation,
                              resource,
                              percentComplete:
                                task.resourcePercentComplete === 100 ? 0 : 100,
                              allocatedHours: userAllocation.allocatedHours,
                            },
                          })
                      : isDone
                        ? editTask({
                            task: { ...taskMock, ...task },
                            nextTask: {
                              ...taskMock,
                              ...task,
                              percentComplete: 0,
                              activityKeyRank: task.keyRank,
                            },
                          })
                        : pushView({
                            key: UseCases.markTaskAsCompleted,
                            data: { task: { ...taskMock, ...task }, project },
                          });
                  }
            }
          >
            <TaskCompletionStatus
              endDate={task.endDate}
              progress={task.percentComplete}
              resource={resource}
              isDisabled={!!resourceRemoved}
              task={{
                ...taskMock,
                ...task,
                allocations: task.allocationData.map((alloc) => ({
                  ...allocation,
                  uuid: alloc.uuid,
                  percentComplete: task.resourcePercentComplete,
                  allocatedHours: task.totalOfAllocatedHours,
                  resource: {
                    ...resource,
                    uuid: alloc.resourceUuid,
                  },
                })),
              }}
            />
          </div>
          <ScrollingText speed={100}>
            <p
              className={styles.name}
              style={resourceRemoved ? { cursor: "default" } : {}}
              onClick={
                resourceRemoved
                  ? undefined
                  : () =>
                      pushView({
                        key: UseCases.retrieveTask,
                        data: {
                          task: { ...taskMock, ...task },
                          isMultiProject: true,
                        },
                      })
              }
            >
              {task.name}
            </p>
          </ScrollingText>
          {resourceRemoved && (
            <span className={styles.removedBadge}>
              {t("timesheetPage.resourceRemoved")}
            </span>
          )}
          {!task.resourceHaveAllocation && (
            <span className={styles.noAllocationBadge}>
              {t("timesheetPage.noAllocation")}
            </span>
          )}
        </div>
        <div className={styles.recordList}>
          <RecordList
            {...task}
            type="task"
            records={task.timeRecords}
            project={project}
            task={{ ...taskMock, ...task }}
            resource={resource}
            allocatedHours={task.totalOfAllocatedHours}
            spentTime={task.totalOfSpentTime}
            spentTimeInWeek={task.totalOfSpentTimeInWeek}
            remainingTime={task.totalOfRemainingTime}
            areDetailsDisplayed={areDetailsDisplayed}
            progression={task.resourcePercentComplete}
            isReloading={isReloading}
            showCompletionColumn={showCompletionColumn}
            timesheetDeadline={timesheetDeadline}
            resourceAllocation={userAllocation}
          />
        </div>
      </div>
    </div>
  );
}

export { Task };
